import {computed} from "vue";
import type {
    BillingAccountStatus,
    PlanType,
    SubscriptionStatus,
    Currency,
    PendingInvoice
} from "~/types/subscription.types";
import {useStream} from "~/stores/_archetypes/rsocket";


export const useSubscription = () => {
    const router = useRouter()
    const workspaceId = router.currentRoute.value.params.workspaceId as string

    const streamState = useStream<BillingAccountStatus, BillingAccountStatus, BillingAccountStatus>(
        'subscription/stream',
        {workspaceId},
        data => data,
        (data, state) => data
    );


    return {
        status: streamState.item as Ref<BillingAccountStatus>,
        loading: streamState.loading,
        error: streamState.error,
    }
}

export const useSubscriptionForEnvironment = () => {
    const {status, loading, error} = useSubscription()
    const {environment} = useEnvironment()


    const plan = computed<PlanInformation | null>(() => {
        if (status.value == null) {
            return null
        }
        const foundComponent = status.value.components.find(component =>
            component.componentId.type === "ENVIRONMENT" && component.componentId.id === environment.value.identifier);
        if (foundComponent == null || !foundComponent.plans['AXONIQ_CONSOLE_PRO']) {
            return null
        }
        return {
            type: 'AXONIQ_CONSOLE_PRO',
            connections: foundComponent.plans['AXONIQ_CONSOLE_PRO'] as number,
            status: foundComponent.subscriptionStatus,
            dailyUpdatesLeft: status.value.dailyComponentUpdateLimit - foundComponent.dailyUpdates,
            dailyUpdatesLimit: status.value.dailyComponentUpdateLimit,
            updatesBlocked: foundComponent.updatesBlocked,
        }
    })

    const pendingInvoices = computed<PendingInvoice[]>(() => {
        return status.value?.pendingInvoices ?? []
    })

    const balance = computed<number | null>(() => {
        return status.value?.balance
    })

    const active = computed<boolean>(() => {
        return status?.value?.active === true
    })

    const currency = computed<Currency | null>(() => {
        return status.value?.currency ?? null
    })
    const currencyShort = computed<string | null>(() => {
        if(currency.value === 'USD') {
            return '$'
        }
        if(currency.value === 'EUR') {
            return '€'
        }
        return '$'
    })
    const paymentMethod = computed<string | null>(() => {
        return status.value?.paymentMethod
    })
    const invoiceBased = computed<boolean>(() => {
        return status.value?.invoiceBased === true
    })

    const firstGraceDate = computed(() => pendingInvoices.value.reduce((min, invoice) => {
            return invoice.dueDate < min ? invoice.dueDate : min
        }, Number.MAX_SAFE_INTEGER)
    );

    return {
        active,
        balance,
        plan,
        loading,
        error,
        currency,
        currencyShort,
        paymentMethod,
        pendingInvoices,
        invoiceBased,
        firstGraceDate,
    }
}

export type PlanInformation = {
    type: PlanType,
    connections: number,
    status: SubscriptionStatus,
    dailyUpdatesLeft: number,
    dailyUpdatesLimit: number,
    updatesBlocked: boolean,
}